import { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import classnames from "classnames";
import validator from "validator";
import { gql, ApolloError, useMutation } from "@apollo/client";
import Layout from "../../components/Layout";
import LayoutCard from "../../components/LayoutCard";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { Heading, Text } from "../../components/Type";
import {
  CheckMarkRounded,
  Dot,
  Info,
  ArrowRight,
} from "../../components/Icons/general-interface-1";
import styles from "./index.module.scss";
import { useStoreContext } from "../../contexts/StoreProvider";

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: ChangePasswordInput) {
    customChangePassword(input: $input) {
      status
      id
    }
  }
`;

const ChangePassword = () => {
  const {
    state: { user },
  } = useStoreContext();
  const history = useHistory();
  const methods = useForm();
  const [error, setError] = useState<string | undefined>();
  const [passwordScore, setPasswordScore] = useState<string>("000");
  const { errors } = methods.formState;
  const MIN_PASSWORD_LENGTH = 7;
  const hasLength = passwordScore.charAt(2) === "3";
  const hasUpper = passwordScore.charAt(1) === "3";
  const hasSymbol = passwordScore.charAt(0) === "3";

  // mutation to update user password
  const [changePassword, { data, loading }] = useMutation(CHANGE_PASSWORD, {
    onCompleted: (data) => {
      if (!data.customChangePassword.status) {
        setError(data.customChangePassword.message);
      }
    },
    onError: (error: ApolloError) => {
      setError(
        error.graphQLErrors?.[0]?.extensions?.exception?.data?.data[0]
          ?.messages[0]?.message || "An unknown error has occurred"
      );
    },
  });

  const onSubmit = async (values) => {
    setError(undefined);
    await changePassword({
      variables: {
        input: {
          id: user?.id,
          password: values.password,
          newPassword: values.newPassword,
          passwordConfirmation: values.passwordConfirmation,
        },
      },
    });
  };

  const getPasswordScore = (value: string) => {
    if (!value) return "000";
    let score = validator.isStrongPassword(value, {
      returnScore: true,
      pointsPerUnique: 0,
      pointsPerRepeat: 0,
      pointsForContainingLower: 0,
      pointsForContainingUpper: 30,
      pointsForContainingNumber: 0,
      pointsForContainingSymbol: 300,
    }) as unknown as number;
    if (value.length >= MIN_PASSWORD_LENGTH) {
      score += 3;
    }
    return score.toString().padStart(3, "0");
  };

  const testPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const score = getPasswordScore(e.currentTarget.value);
    setPasswordScore(score);
  };

  const handleDashboard = () => {
    history.push("/dashboard");
    return null;
  };

  return (
    <Layout>
      <Helmet>
        <title>Motion EASI Change Password</title>
      </Helmet>
      <LayoutCard
        error={error}
        header={
          <Heading size="h4">
            {data && data.customChangePassword.status
              ? "Successful!"
              : "Update your password"}
          </Heading>
        }
      >
        {data && data.customChangePassword.status ? (
          <>
            <Text className={styles.successMessage}>
              Your password has been successfully changed.
            </Text>
            <br />
            <div className={styles.successButtonRow}>
              <Button primary icon={<ArrowRight />} onClick={handleDashboard}>
                Go to dashboard
              </Button>
            </div>
          </>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <TextInput
                id="password"
                label="Current Password"
                type="password"
                disableErrors
                showEyeIcon
                fieldOptions={{ required: "Current password is required" }}
                disabled={loading}
              />
              <TextInput
                id="newPassword"
                label="New Password"
                type="password"
                showEyeIcon
                disableErrors
                disabled={loading}
                fieldOptions={{
                  required: "New password is required",
                  validate: (value) =>
                    getPasswordScore(value) !== "333"
                      ? "A strong password is required"
                      : undefined,
                }}
                onChange={testPassword}
              />
              <div className={styles.validations}>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasLength && styles.valid
                  )}
                >
                  {hasLength ? <CheckMarkRounded /> : <Dot />} 7 characters
                  minimum
                </Text>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasSymbol && styles.valid
                  )}
                >
                  {hasSymbol ? <CheckMarkRounded /> : <Dot />} One special
                  character
                </Text>
                <Text
                  size="small"
                  className={classnames(
                    styles.validation,
                    hasUpper && styles.valid
                  )}
                >
                  {hasUpper ? <CheckMarkRounded /> : <Dot />} One uppercase
                  character
                </Text>
              </div>
              <TextInput
                id="passwordConfirmation"
                label="Confirm New Password"
                type="password"
                showEyeIcon
                disableErrors
                disabled={loading}
                fieldOptions={{
                  required: "New password confirmation is required",
                  validate: {
                    match: (value) => {
                      const newPassword = methods.getValues("newPassword");
                      const password = methods.getValues("password");
                      return newPassword !== value
                        ? "Passwords do not match"
                        : password === value
                        ? "New password must be different than current password"
                        : undefined;
                    },
                  },
                }}
              />
              {errors.password ||
              errors.newPassword ||
              errors.passwordConfirmation ? (
                <div className={styles.error}>
                  <Info />
                  {errors.password
                    ? errors.password.message
                    : errors.newPassword
                    ? errors.newPassword.message
                    : errors.passwordConfirmation &&
                      errors.passwordConfirmation.message}
                </div>
              ) : null}
              <div className={styles.buttonRow}>
                <Button primary submit icon={<ArrowRight />} disabled={loading}>
                  Update
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </LayoutCard>
    </Layout>
  );
};

export default ChangePassword;
